import React, { useRef, useState } from "react";
import { FaPlus, FaTrash, FaUpload } from "react-icons/fa";
import { toast } from "react-toastify";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import { uploadCmsPageImage } from "../../services/cmsUpload.service";
import { DEFAULT_CONTACT_CMS_CONFIG } from "../../utils/contactPageCms";

function Field({ label, children, hint }) {
  return (
    <div className="cms-contact-editor__field">
      <label className="cms-contact-editor__label">{label}</label>
      {children}
      {hint ? <p className="cms-contact-editor__hint">{hint}</p> : null}
    </div>
  );
}

function updateListItem(list, index, key, value) {
  return list.map((row, i) => (i === index ? { ...row, [key]: value } : row));
}

const EMPTY_EMAIL_ROW = { label: "", email: "" };
const ALLOWED_EXT = new Set(["jpg", "jpeg", "png", "webp"]);
const MAX_BYTES = 2 * 1024 * 1024;

export default function ContactCmsEditor({ config, onChange, readOnly = false }) {
  const data = config || DEFAULT_CONTACT_CMS_CONFIG;
  const emails = Array.isArray(data.directEmails) ? data.directEmails : [];
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const patch = (partial) => onChange({ ...data, ...partial });

  const addEmail = () => {
    if (readOnly) return;
    patch({ directEmails: [...emails, { ...EMPTY_EMAIL_ROW }] });
  };

  const removeEmail = (index) => {
    if (readOnly) return;
    patch({ directEmails: emails.filter((_, i) => i !== index) });
  };

  const validateFile = (file) => {
    const ext = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
    if (!ALLOWED_EXT.has(ext)) {
      toast.error("Only JPG, JPEG, PNG and WEBP images are allowed.");
      return false;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Illustration image must be 2 MB or smaller.");
      return false;
    }
    return true;
  };

  const handleBackgroundUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || readOnly || !validateFile(file)) return;

    setUploading(true);
    try {
      const imagePath = await uploadCmsPageImage(file, { fileType: "contact_hero_illustration" });
      patch({ heroBackgroundUrl: imagePath });
      toast.success("Hero illustration uploaded. Click Save to publish.");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Hero illustration upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const clearBackground = () => {
    if (readOnly) return;
    patch({ heroBackgroundUrl: "" });
  };

  const previewUrl = data.heroBackgroundUrl ? resolvePublicFileUrl(data.heroBackgroundUrl) : "";

  return (
    <div className="cms-contact-editor">
      <p className="cms-contact-editor__intro">
        Configure the <strong>Reach us directly</strong> section and hero illustration on{" "}
        <strong>/contact</strong>. Enquiry form, stats, and map stay fixed on the page.
      </p>

      <section className="cms-contact-editor__section">
        <h3 className="cms-contact-editor__section-title">Hero illustration image</h3>
        <Field
          label="Hero illustration"
          hint="Replaces the headphones graphic on the right side of the Contact Us hero. JPG, PNG, or WEBP up to 2 MB."
        >
          <div className="cms-contact-editor__upload-row">
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
              hidden
              disabled={readOnly || uploading}
              onChange={handleBackgroundUpload}
            />
            <button
              type="button"
              className="cms-contact-editor__add-btn"
              disabled={readOnly || uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              <FaUpload aria-hidden />
              {uploading ? "Uploading…" : "Upload image"}
            </button>
            {data.heroBackgroundUrl ? (
              <button
                type="button"
                className="cms-contact-editor__delete-btn"
                disabled={readOnly || uploading}
                onClick={clearBackground}
              >
                <FaTrash aria-hidden />
                Remove
              </button>
            ) : null}
          </div>
        </Field>
        {previewUrl ? (
          <div className="cms-contact-editor__preview">
            <img src={previewUrl} alt="Contact hero illustration preview" />
          </div>
        ) : (
          <p className="cms-contact-editor__empty">No custom illustration uploaded. Default graphic is used.</p>
        )}
      </section>

      <section className="cms-contact-editor__section">
        <h3 className="cms-contact-editor__section-title">Reach us directly</h3>
        <Field label="Section heading">
          <input
            type="text"
            className="cms-contact-editor__input"
            value={data.asideHeading || ""}
            disabled={readOnly}
            onChange={(e) => patch({ asideHeading: e.target.value })}
          />
        </Field>

        <div className="cms-contact-editor__emails-head">
          <h4 className="cms-contact-editor__emails-title">Emails</h4>
          {!readOnly ? (
            <button type="button" className="cms-contact-editor__add-btn" onClick={addEmail}>
              <FaPlus aria-hidden />
              Add more
            </button>
          ) : null}
        </div>

        {emails.length === 0 ? (
          <p className="cms-contact-editor__empty">No emails yet. Click Add more to create one.</p>
        ) : (
          <div className="cms-contact-editor__subgrid">
            {emails.map((item, index) => (
              <div key={`email-${index}`} className="cms-contact-editor__card">
                <div className="cms-contact-editor__card-head">
                  <span className="cms-contact-editor__card-title">Email {index + 1}</span>
                  {!readOnly ? (
                    <button
                      type="button"
                      className="cms-contact-editor__delete-btn"
                      onClick={() => removeEmail(index)}
                      aria-label={`Delete email ${index + 1}`}
                      title="Delete"
                    >
                      <FaTrash aria-hidden />
                      Delete
                    </button>
                  ) : null}
                </div>
                <Field label="Label">
                  <input
                    type="text"
                    className="cms-contact-editor__input"
                    value={item.label || ""}
                    disabled={readOnly}
                    placeholder="e.g. General"
                    onChange={(e) =>
                      patch({
                        directEmails: updateListItem(emails, index, "label", e.target.value),
                      })
                    }
                  />
                </Field>
                <Field label="Address">
                  <input
                    type="email"
                    className="cms-contact-editor__input"
                    value={item.email || ""}
                    disabled={readOnly}
                    placeholder="name@trocanet.com"
                    onChange={(e) =>
                      patch({
                        directEmails: updateListItem(emails, index, "email", e.target.value),
                      })
                    }
                  />
                </Field>
              </div>
            ))}
          </div>
        )}

        {!readOnly && emails.length > 0 ? (
          <div className="cms-contact-editor__emails-footer">
            <button type="button" className="cms-contact-editor__add-btn" onClick={addEmail}>
              <FaPlus aria-hidden />
              Add more
            </button>
          </div>
        ) : null}

        <Field label="Office address">
          <textarea
            rows={2}
            className="cms-contact-editor__textarea"
            value={data.officeAddress || ""}
            disabled={readOnly}
            onChange={(e) => patch({ officeAddress: e.target.value })}
          />
        </Field>
        <Field label="Support hours">
          <input
            type="text"
            className="cms-contact-editor__input"
            value={data.supportHours || ""}
            disabled={readOnly}
            onChange={(e) => patch({ supportHours: e.target.value })}
          />
        </Field>
      </section>
    </div>
  );
}
